import React from "react";
import {
  createStyles,
  Grid,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";
import CardCommon from "../../../../components/card/CardCommon";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import ValidationMessage from "../../../../components/validation/ValidationMessage";

export interface CustomerNodeProps {
  title: string;
  averageTimeType: string;
  data: {
    warningThreshold: number;
    criticalThreshold: number;
  };
  errorMessage: any;
  setAlertConfigNode: (fn: (prev: any) => any) => void;
  setErrorMessage: any;
  station?: any;
}

const useStyles = makeStyles((theme: CustomTheme) => createStyles({}));

const CardBox: React.FC<CustomerNodeProps> = ({
  title,
  averageTimeType,
  data,
  setAlertConfigNode,
  errorMessage,
  setErrorMessage,
  station,
}) => {
  const getMinutes = (value: number) => Math.floor(value / 60);
  const getSeconds = (value: number) => value % 60;

  const handleTimeChange = (
    field: "warningThreshold" | "criticalThreshold",
    part: "min" | "sec",
    value: string,
  ) => {
    const num = Number(value);
    const otherPart =
      part === "min" ? getSeconds(data[field]) : getMinutes(data[field]);
    const totalSeconds =
      part === "min" ? num * 60 + otherPart : otherPart * 60 + num;

    if ((num < 60 && part === "sec") || part === "min") {
      setErrorMessage({});
      if (averageTimeType === "stationAverageTime") {
        setAlertConfigNode((prev: any) => ({
          ...prev,
          [averageTimeType]: {
            ...prev[averageTimeType],
            [station]: {
              ...((prev[averageTimeType] && prev[averageTimeType][station]) ||
                {}),
              [field]: totalSeconds,
            },
          },
        }));
      } else {
        setAlertConfigNode((prev: any) => ({
          ...prev,
          [averageTimeType]: {
            ...prev[averageTimeType],
            [field]: totalSeconds,
          },
        }));
      }
    }
  };
  return (
    <CardCommon
      backgroundColor="entity_background"
      style={{ minHeight: "218px" }}
    >
      <div style={{ padding: "20px" }}>
        <Typography variant="h6" align="left" style={{ marginBottom: "8px" }}>
          {title || station}
        </Typography>
        <Grid container spacing={2}>
          {/* Warning Threshold - Minutes */}
          <Grid item xs={6}>
            <TextfieldCommon
              id={`${averageTimeType}-warning-min`}
              label="Warning Threshold"
              type="text"
              value={getMinutes(data.warningThreshold)}
              onChange={(e: any) =>
                handleTimeChange("warningThreshold", "min", e.target.value)
              }
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
              style={{ width: "100%" }}
            />

            {averageTimeType === "stationAverageTime"
              ? errorMessage?.[station].warningThreshold && (
                  <ValidationMessage
                    message={"Warning Threshold is required"}
                  />
                )
              : errorMessage?.warningThreshold && (
                  <ValidationMessage
                    message={"Warning Threshold is required"}
                  />
                )}
          </Grid>
          {/* Warning Threshold - Seconds */}
          <Grid item xs={6}>
            <TextfieldCommon
              id={`${averageTimeType}-warning-sec`}
              label="Warning Threshold"
              type="text"
              value={getSeconds(data.warningThreshold)}
              onChange={(e: any) =>
                handleTimeChange("warningThreshold", "sec", e.target.value)
              }
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">sec</InputAdornment>
                ),
              }}
              style={{ width: "100%", marginBottom: "1rem" }}
            />
          </Grid>
          {/* Critical Threshold - Minutes */}
          <Grid item xs={6}>
            <TextfieldCommon
              id={`${averageTimeType}-critical-min`}
              label="Critical Threshold"
              type="text"
              value={getMinutes(data.criticalThreshold)}
              onChange={(e: any) =>
                handleTimeChange("criticalThreshold", "min", e.target.value)
              }
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
              style={{ width: "100%" }}
            />
            {averageTimeType === "stationAverageTime"
              ? errorMessage?.[station].criticalThreshold && (
                  <ValidationMessage
                    message={"Critical Threshold is required"}
                  />
                )
              : errorMessage?.criticalThreshold && (
                  <ValidationMessage
                    message={"Critical Threshold is required"}
                  />
                )}
          </Grid>
          {/* Critical Threshold - Seconds */}
          <Grid item xs={6}>
            <TextfieldCommon
              id={`${averageTimeType}-critical-sec`}
              label="Critical Threshold"
              type="text"
              value={getSeconds(data.criticalThreshold)}
              onChange={(e: any) =>
                handleTimeChange("criticalThreshold", "sec", e.target.value)
              }
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">sec</InputAdornment>
                ),
                max: 59,
              }}
              style={{ width: "100%" }}
            />
          </Grid>{" "}
        </Grid>
      </div>
    </CardCommon>
  );
};

export default CardBox;
